import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { showNotification } from './Notification';

const StatTiles = ({ totals }) => (
  <div className="report-totals">
    <div className="stat-tile"><span>{totals.total}</span><small>Total records</small></div>
    <div className="stat-tile ok"><span>{totals.complete}</span><small>Complete</small></div>
    <div className="stat-tile warn"><span>{totals.partial}</span><small>Partial</small></div>
    <div className="stat-tile muted"><span>{totals.none}</span><small>Not started</small></div>
    <div className="stat-tile"><span>{totals.completion_rate}%</span><small>Completion rate</small></div>
  </div>
);

const AssessmentReport = () => {
  const [assessorsList, setAssessorsList] = useState([]);
  const [unitsCount, setUnitsCount] = useState(12);
  const [unitFilter, setUnitFilter] = useState('all');
  const [assessorFilter, setAssessorFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [byAssessor, setByAssessor] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    axios.get('/api/assessors').then((res) => setAssessorsList(Array.isArray(res.data) ? res.data : []));
    axios.get('/api/assessment-settings').then((res) => setUnitsCount(res.data.units_count));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (assessorFilter === 'all') {
        const res = await axios.get(`/api/assessment-reports/by-assessor?unit=${unitFilter}`);
        setByAssessor(res.data);
        setDetail(null);
      } else {
        const res = await axios.get(`/api/assessment-reports/assessor/${assessorFilter}?unit=${unitFilter}`);
        setDetail(res.data);
        setByAssessor(null);
      }
    } catch (error) {
      showNotification(error.response?.data?.error || 'Error loading report', 'error');
    } finally {
      setLoading(false);
    }
  }, [unitFilter, assessorFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const unitOptions = Array.from({ length: unitsCount }, (_, i) => i + 1);

  return (
    <div className="assessment-report">
      <div className="report-filters">
        <div className="form-group">
          <label>Unit</label>
          <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
            <option value="all">All Units</option>
            {unitOptions.map((u) => <option key={u} value={u}>Unit {u}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Assessor</label>
          <select value={assessorFilter} onChange={(e) => setAssessorFilter(e.target.value)}>
            <option value="all">All Assessors</option>
            {assessorsList.map((a) => (
              <option key={a.id} value={a.id}>{[a.surname, a.first_name, a.other_name].filter(Boolean).join(' ')}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading report...</div>
      ) : byAssessor ? (
        <>
          <StatTiles totals={byAssessor.totals} />
          <div className="assessment-table-wrap">
            <table className="assessment-table">
              <thead>
                <tr>
                  <th>Assessor</th><th>Specialization</th><th>Trainees</th>
                  <th>Complete</th><th>Partial</th><th>Not started</th><th>Completion %</th>
                </tr>
              </thead>
              <tbody>
                {byAssessor.assessors.length === 0 ? (
                  <tr><td colSpan={7}>No assessors yet.</td></tr>
                ) : byAssessor.assessors.map((a) => (
                  <tr key={a.id} className={a.is_restricted ? 'restricted-row' : ''}>
                    <td data-label="Assessor">{a.name}</td>
                    <td data-label="Specialization"><span className="chip">{a.specialization}</span></td>
                    <td data-label="Trainees">{a.trainee_count}</td>
                    <td data-label="Complete">{a.complete}</td>
                    <td data-label="Partial">{a.partial}</td>
                    <td data-label="Not started">{a.none}</td>
                    <td data-label="Completion %">{a.completion_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : detail ? (
        <>
          <h3 className="report-detail-title">{detail.assessor.name} — <span className="chip">{detail.assessor.specialization}</span></h3>
          <StatTiles totals={detail.totals} />
          <div className="assessment-table-wrap">
            <table className="assessment-table">
              <thead>
                <tr>
                  <th>Trainee</th><th>Complete</th><th>Partial</th><th>Not started</th><th>Completion %</th>
                </tr>
              </thead>
              <tbody>
                {detail.trainees.length === 0 ? (
                  <tr><td colSpan={5}>No trainees currently assigned to this assessor.</td></tr>
                ) : detail.trainees.map((t) => (
                  <tr key={t.id}>
                    <td data-label="Trainee">{t.name}</td>
                    <td data-label="Complete">{t.complete}</td>
                    <td data-label="Partial">{t.partial}</td>
                    <td data-label="Not started">{t.none}</td>
                    <td data-label="Completion %">{t.completion_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AssessmentReport;
